import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import DeckleImage from '@/components/generals/deckle-image'
import Reveal from '@/components/generals/reveal'

const CraftStory: FC = (): ReactNode => {
    return (
        <section id='story' aria-labelledby='story-title' className='container-page py-20 md:py-32'>
            <div className='grid gap-14 lg:grid-cols-12 lg:gap-8'>
                <Reveal className='lg:col-span-5'>
                    <DeckleImage
                        src='/images/qogoz/craft-manuscript.jpg'
                        alt='An old manuscript on polished cream paper beside a reed pen and a brass ink pot'
                        sizes='(min-width: 1024px) 38vw, 92vw'
                        className='sm:w-[80%] lg:w-full'
                    />
                    <p className='mt-6 max-w-sm font-heading text-[0.95rem] italic text-bark-soft'>
                        Fig. 2. A scribe working a thousand years ago would recognise this surface at once.
                    </p>
                </Reveal>

                <div className='lg:col-span-6 lg:col-start-7 lg:pt-16'>
                    <Reveal>
                        <p className='flex items-center gap-4 text-sm font-medium text-bark-soft'>
                            <span aria-hidden='true' className='h-px w-10 bg-pomegranate' />
                            The story of the craft
                        </p>
                        <h2 id='story-title' className='mt-6 font-heading text-[clamp(2.2rem,4.4vw,3.8rem)] font-light leading-[1.04] tracking-[-0.02em] text-bark'>
                            When Samarkand taught the world to write on paper
                        </h2>
                    </Reveal>

                    <Reveal delay={120} className='mt-10 space-y-6 text-[1.06rem] leading-[1.8] text-bark-soft'>
                        <p className='drop-cap text-bark'>
                            In the year 751, after a battle on the Talas river, the story goes that a few Chinese papermakers were brought to Samarkand as prisoners. They carried a secret the West did not yet know. Within a few generations the city&rsquo;s mills were turning mulberry bark into sheets so smooth that scribes from Baghdad to Cairo wanted nothing else.
                        </p>
                        <p>
                            By the nineteenth century the water wheels had stopped. The craft came back only in our own lifetime, rebuilt from old manuscripts and the memories of a few elderly masters. Our atelier is one small part of that return.
                        </p>
                    </Reveal>

                    <Reveal delay={200}>
                        <blockquote className='mt-12 border-l-2 border-pomegranate pl-6'>
                            <p className='font-heading text-[1.45rem] font-light italic leading-snug text-bark'>
                                &ldquo;Among the specialities of Samarkand is paper, which has driven out the papyrus and parchment that were used before.&rdquo;
                            </p>
                            <footer className='mt-4 text-sm text-bark-soft'>
                                Al-Tha&rsquo;alibi, writing in the eleventh century
                            </footer>
                        </blockquote>

                        <Link href='/craft' className='mt-12 inline-flex items-center gap-2 font-medium text-pomegranate'>
                            <span className='ink-link pb-0.5'>Read the full story and meet the makers</span>
                            <ArrowRight className='size-4' strokeWidth={1.75} />
                        </Link>
                    </Reveal>
                </div>
            </div>
        </section>
    )
}

export default CraftStory
